/* Mock cart — localStorage-backed shopping cart with coupon support.
 *
 * Cart shape persisted under `nethrasap.mock.v1.cart`:
 *   {
 *     items:   [{ id, product_slug, qty, added_at }],
 *     coupon:  Coupon | null,
 *     owner:   "anon" | "<user_id>",
 *   }
 *
 * Reads enrich every line with product detail before returning. The catch:
 * stored lines reference products by slug only, so the catalogue stays the
 * source of truth.
 */

import * as store from "@/data/mocks/storage";
import { PRODUCTS } from "@/data/mocks/products";
import { withLatency, withError } from "./_util";

const EMPTY = { items: [], coupon: null, owner: "anon" };

function readCart() {
  return store.get("cart", EMPTY) || EMPTY;
}

function writeCart(next) {
  store.set("cart", next);
  return next;
}

function findProductBySlug(slug) {
  const created = store.get("products_created", []) || [];
  return PRODUCTS.find((p) => p.slug === slug) || created.find((p) => p.slug === slug) || null;
}

function unitPriceFor(product) {
  if (!product) return 0;
  return product.price_min ?? product.min ?? 0;
}

function enrichLine(line) {
  const p = findProductBySlug(line.product_slug);
  const unit = unitPriceFor(p);
  return {
    id: line.id,
    product_slug: line.product_slug,
    product_name: p?.name || line.product_slug,
    product_brand: p?.brand || "",
    product_unit_label: p?.unit_label || p?.unit || "",
    product_stock_status: p?.stock_status || p?.stock || "in_stock",
    product_badge: p?.badge || null,
    product_delivery_time_mins: p?.delivery_time_mins ?? p?.delivery ?? null,
    product_image_key: "/elementor-placeholder-image.png",
    qty: line.qty,
    unit_price: unit,
    line_total: unit * line.qty,
  };
}

function computeTotals(items, coupon) {
  const subtotal = items.reduce((s, it) => s + it.line_total, 0);
  const gst_amount = Math.round(subtotal * 0.05);
  const shipping = subtotal >= 2000 || subtotal === 0 ? 0 : 99;
  let discount = 0;
  if (coupon) {
    if (coupon.percent_off) {
      discount = Math.round(subtotal * (coupon.percent_off / 100));
      if (coupon.max_discount) discount = Math.min(discount, coupon.max_discount);
    } else if (coupon.flat_off) {
      discount = coupon.flat_off;
    }
    if (discount > subtotal) discount = subtotal;
  }
  const total = Math.max(0, subtotal + gst_amount + shipping - discount);
  return { subtotal, gst_amount, shipping, discount, total };
}

function buildResponse(cart) {
  const items = (cart.items || []).map(enrichLine);
  const totals = computeTotals(items, cart.coupon);
  return {
    items,
    coupon: cart.coupon ? { code: cart.coupon.code, discount: totals.discount } : null,
    ...totals,
    item_count: items.reduce((s, i) => s + i.qty, 0),
    owner: cart.owner || "anon",
  };
}

function makeLineId() {
  return "ci-" + Math.random().toString(36).slice(2, 10);
}

export async function get() {
  return withLatency(buildResponse(readCart()));
}

export async function add({ slug, qty = 1 } = {}) {
  if (!slug) return withError("slug required", 400);
  const product = findProductBySlug(slug);
  if (!product) return withError("Product not found", 404);
  const cart = readCart();
  const items = cart.items.slice();
  const existing = items.find((it) => it.product_slug === slug);
  if (existing) {
    existing.qty = Math.min(99, existing.qty + qty);
  } else {
    items.push({ id: makeLineId(), product_slug: slug, qty, added_at: Date.now() });
  }
  writeCart({ ...cart, items });
  return withLatency(buildResponse({ ...cart, items }));
}

export async function update({ item_id, qty } = {}) {
  if (!item_id) return withError("item_id required", 400);
  const cart = readCart();
  let items = cart.items.slice();
  if (qty <= 0) {
    items = items.filter((it) => it.id !== item_id);
  } else {
    items = items.map((it) => (it.id === item_id ? { ...it, qty: Math.min(99, qty) } : it));
  }
  writeCart({ ...cart, items });
  return withLatency(buildResponse({ ...cart, items }));
}

export async function remove({ item_id } = {}) {
  if (!item_id) return withError("item_id required", 400);
  const cart = readCart();
  const items = cart.items.filter((it) => it.id !== item_id);
  writeCart({ ...cart, items });
  return withLatency(buildResponse({ ...cart, items }));
}

export async function applyCoupon({ code } = {}) {
  const cart = readCart();
  if (!code) return withError("Coupon code required", 400);
  const all = store.get("coupons", []) || [];
  const normalized = String(code).trim().toUpperCase();
  const coupon = all.find((c) => c.code.toUpperCase() === normalized);
  if (!coupon) return withError("Invalid coupon code", 400);

  const items = cart.items.map(enrichLine);
  const subtotal = items.reduce((s, it) => s + it.line_total, 0);
  if (coupon.min_order && subtotal < coupon.min_order) {
    return withError(`Minimum order ₹${coupon.min_order} required for ${coupon.code}`, 400);
  }

  // Role gate (RETAILER100 etc.)
  if (coupon.role) {
    const session = store.get("session", null);
    if (!session || session.role !== coupon.role) {
      return withError(`${coupon.code} is restricted to ${coupon.role} accounts`, 403);
    }
  }

  writeCart({ ...cart, coupon });
  return withLatency(buildResponse({ ...cart, coupon }));
}

export async function removeCoupon() {
  const cart = readCart();
  writeCart({ ...cart, coupon: null });
  return withLatency(buildResponse({ ...cart, coupon: null }));
}

export async function clear() {
  const cart = readCart();
  writeCart({ ...cart, items: [], coupon: null });
  return withLatency(buildResponse({ ...cart, items: [], coupon: null }));
}

/** Internal: claim anonymous cart for a freshly logged-in user. */
export async function claim({ user_id } = {}) {
  const cart = readCart();
  if (cart.items.length === 0 && cart.owner === "anon") return withLatency(buildResponse(cart));
  if (cart.owner === "anon" && user_id) {
    writeCart({ ...cart, owner: user_id });
  }
  return withLatency(buildResponse(readCart()));
}

export async function release() {
  // Called on logout — flip owner back to anon without dropping items.
  const cart = readCart();
  writeCart({ ...cart, owner: "anon" });
  return withLatency(buildResponse(readCart()));
}
